import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth, can } from "@/context/AuthContext";
import { NewTrainingDialog } from "@/components/NewTrainingDialog";
import { EmployeePortal } from "@/pages/EmployeePortal";
import { StatusBadge, ProgressBar, EmptyState, fmtDate, fmtDateTime } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Activity, CheckCircle2, CalendarClock, Timer, Search,
  ClipboardList, FileText, BookOpen, ArrowRight, Trash2, Loader2, Eye,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, testid, accent }) => (
  <Card className="p-5 flex items-center gap-4" data-testid={testid}>
    <div className={`flex items-center justify-center h-12 w-12 rounded-xl ${accent}`}>
      <Icon size={22} />
    </div>
    <div>
      <div className="text-2xl font-heading font-extrabold font-mono tabular">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showNew, setShowNew] = useState(false);
  const [empPreview, setEmpPreview] = useState(false);
  const qc = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const deleteMutation = useMutation({
    mutationFn: async (id) => api.delete(`/training-sessions/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sessions"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      setDeleteTarget(null);
      toast.success("Training deleted");
    },
    onError: () => toast.error("Could not delete training"),
  });
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState("all");
  const [ttype, setTtype] = useState("all");
  const [showAll, setShowAll] = useState(false);

  const { data: stats } = useQuery({
    queryKey: ["stats"], queryFn: async () => (await api.get("/training-sessions/stats")).data,
  });
  const { data: settings } = useQuery({
    queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data,
  });
  const { data: recent } = useQuery({
    queryKey: ["recent-materials"], queryFn: async () => (await api.get("/materials/recent")).data,
  });

  const filters = { showAll };
  if (position !== "all") filters.position = position;
  if (ttype !== "all") filters.training_type = ttype;
  if (search) filters.q = search;

  const { data: sessions = [] } = useQuery({
    queryKey: ["sessions", filters],
    queryFn: async () => (await api.get("/training-sessions", { params: filters })).data,
    refetchInterval: 5000,
  });

  const inProgress = sessions.filter((s) => s.status === "in_progress");
  const completed = sessions.filter((s) => s.status === "completed");
  const canSeeAll = can(user, "see_all_sessions");

  if (user?.role === "employee") return <EmployeePortal />;
  if (empPreview) return <EmployeePortal preview onExit={() => setEmpPreview(false)} />;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back, {user?.name?.split(" ")[0]}. Here's your training overview.</p>
        </div>
        {can(user, "create_session") && (
          <div className="flex gap-2">
            {can(user, "manage_users") && (
              <Button variant="outline" data-testid="view-as-employee-button" onClick={() => setEmpPreview(true)} className="gap-2 h-11 rounded-xl">
                <Eye size={18} /> View as Employee
              </Button>
            )}
            <Button data-testid="start-new-training-button" onClick={() => setShowNew(true)} className="gap-2 h-11 rounded-xl">
              <Plus size={18} /> Start New Training
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-in-progress-card" icon={Activity} label="In Progress" value={stats?.in_progress ?? "—"} accent="bg-warning/15 text-warning" />
        <StatCard testid="stat-completed-month-card" icon={CalendarClock} label="Completed This Month" value={stats?.completed_this_month ?? "—"} accent="bg-accent/20 text-accent" />
        <StatCard testid="stat-completed-all-card" icon={CheckCircle2} label="Completed All Time" value={stats?.completed_all_time ?? "—"} accent="bg-success/15 text-success" />
        <StatCard testid="stat-avg-days-card" icon={Timer} label="Avg Days to Complete" value={stats?.avg_days_to_complete ?? "—"} accent="bg-primary/10 text-primary" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-9 space-y-4">
          {/* Filters */}
          <Card className="p-4 flex flex-col sm:flex-row gap-3 flex-wrap items-stretch sm:items-center">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <Input data-testid="search-trainee-input" placeholder="Search trainee name…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={position} onValueChange={setPosition}>
              <SelectTrigger className="w-full sm:w-[170px]" data-testid="filter-position-select"><SelectValue placeholder="Position" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Positions</SelectItem>
                {settings?.positions?.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={ttype} onValueChange={setTtype}>
              <SelectTrigger className="w-full sm:w-[190px]" data-testid="filter-type-select"><SelectValue placeholder="Training Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {settings?.training_types?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            {canSeeAll && (
              <div className="flex items-center gap-2">
                <Switch id="show-all" checked={showAll} onCheckedChange={setShowAll} data-testid="show-all-toggle" />
                <Label htmlFor="show-all" className="text-sm text-muted-foreground whitespace-nowrap">Show all</Label>
              </div>
            )}
          </Card>

          <Tabs defaultValue="in_progress">
            <TabsList data-testid="dashboard-tabs">
              <TabsTrigger value="in_progress" data-testid="tab-in-progress">In Progress ({inProgress.length})</TabsTrigger>
              <TabsTrigger value="completed" data-testid="tab-completed">Completed ({completed.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="in_progress">
              <Card className="overflow-hidden">
                {inProgress.length === 0 ? (
                  <EmptyState icon={ClipboardList} title="No trainings in progress" desc="Start a new training to begin tracking a trainee's onboarding." />
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Trainee</TableHead>
                          <TableHead>Position</TableHead>
                          <TableHead className="hidden md:table-cell">Type</TableHead>
                          <TableHead className="hidden lg:table-cell">Trainer</TableHead>
                          <TableHead className="hidden lg:table-cell">Last Activity</TableHead>
                          <TableHead className="w-[180px]">Progress</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {inProgress.map((s) => (
                          <TableRow key={s.id} data-testid={`session-row-${s.id}`} className="cursor-pointer" onClick={() => navigate(`/sessions/${s.id}`)}>
                            <TableCell className="font-medium">
                              {s.trainee_name}
                              <div className="text-[11px] text-muted-foreground font-mono">{s.session_number}</div>
                            </TableCell>
                            <TableCell>{s.trainee_position}</TableCell>
                            <TableCell className="hidden md:table-cell text-muted-foreground">{s.training_type}</TableCell>
                            <TableCell className="hidden lg:table-cell text-muted-foreground">{s.trainer_name}</TableCell>
                            <TableCell className="hidden lg:table-cell text-muted-foreground font-mono text-xs">{fmtDateTime(s.last_saved_at)}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <ProgressBar value={s.percent_complete} />
                                <span className="font-mono text-xs w-9 text-right">{s.percent_complete}%</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 justify-end">
                                <Button size="sm" variant="secondary" data-testid={`resume-button-${s.id}`} className="gap-1" onClick={(e) => { e.stopPropagation(); navigate(`/sessions/${s.id}`); }}>
                                  Resume <ArrowRight size={14} />
                                </Button>
                                {can(user, "delete_session") && (
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" data-testid={`delete-session-${s.id}`} onClick={(e) => { e.stopPropagation(); setDeleteTarget(s); }}>
                                    <Trash2 size={14} />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="completed">
              <Card className="overflow-hidden">
                {completed.length === 0 ? (
                  <EmptyState icon={CheckCircle2} title="No completed trainings yet" desc="Completed and signed-off trainings will appear here." />
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Trainee</TableHead>
                          <TableHead>Position</TableHead>
                          <TableHead className="hidden md:table-cell">Type</TableHead>
                          <TableHead className="hidden lg:table-cell">Trainer</TableHead>
                          <TableHead className="hidden md:table-cell">Completed</TableHead>
                          <TableHead className="hidden lg:table-cell">Days</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {completed.map((s) => (
                          <TableRow key={s.id} data-testid={`session-row-${s.id}`} className="cursor-pointer" onClick={() => navigate(`/sessions/${s.id}`)}>
                            <TableCell className="font-medium">
                              {s.trainee_name}
                              <div className="text-[11px] text-muted-foreground font-mono">{s.session_number}</div>
                            </TableCell>
                            <TableCell>{s.trainee_position}</TableCell>
                            <TableCell className="hidden md:table-cell text-muted-foreground">{s.training_type}</TableCell>
                            <TableCell className="hidden lg:table-cell text-muted-foreground">{s.trainer_name}</TableCell>
                            <TableCell className="hidden md:table-cell font-mono text-xs">{fmtDate(s.completed_at)}</TableCell>
                            <TableCell className="hidden lg:table-cell font-mono">{s.days_to_complete ?? "—"}</TableCell>
                            <TableCell><StatusBadge status="completed" /></TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 justify-end">
                                <Button size="sm" variant="secondary" className="gap-1" data-testid={`view-session-${s.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/sessions/${s.id}`); }}>
                                  <FileText size={14} /> View
                                </Button>
                                {can(user, "delete_session") && (
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" data-testid={`delete-session-${s.id}`} onClick={(e) => { e.stopPropagation(); setDeleteTarget(s); }}>
                                    <Trash2 size={14} />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Recently added materials */}
        <div className="lg:col-span-3">
          <Card className="p-5" data-testid="recent-materials-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold">Recently Added</h3>
              <Button variant="ghost" size="sm" className="gap-1 text-xs" data-testid="library-link-card" onClick={() => navigate("/library")}>
                Library <ArrowRight size={12} />
              </Button>
            </div>
            {(!recent || recent.length === 0) ? (
              <div className="flex flex-col items-center text-center py-6 text-muted-foreground">
                <BookOpen size={24} className="mb-2" />
                <p className="text-sm">No materials yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recent.map((m) => (
                  <div key={m.id} className="flex items-start gap-3 text-sm cursor-pointer hover:bg-muted/50 rounded-lg p-2 -mx-2" onClick={() => navigate("/library")} data-testid={`recent-material-${m.id}`}>
                    <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-accent/15 text-accent shrink-0">
                      <FileText size={16} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{m.title}</div>
                      <div className="text-[11px] text-muted-foreground uppercase">{m.file_extension}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      <NewTrainingDialog open={showNew} onOpenChange={setShowNew} settings={settings} />

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-session-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete this training?</DialogTitle>
            <DialogDescription>
              This permanently removes {deleteTarget?.trainee_name}'s record ({deleteTarget?.session_number}). This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" className="gap-2" disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate(deleteTarget.id)} data-testid="confirm-delete-session">
              {deleteMutation.isPending && <Loader2 size={16} className="animate-spin" />} Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
